import { useEffect, useState } from "react";
import {
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import logo from "../../../assets/logo-nanthiat.png";

export default function ProspectInteractions({ prospect }) {
  const [showForm, setShowForm] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [formData, setFormData] = useState({
    typeInteraction: "",
    canal: "",
    date: "",
    contenu: "",
    prochaineEtape: "",
    dateProchaineInteraction: "",
    important: false,
  });

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  useEffect(() => {
    if (prospect?._id) fetchInteractions();
  }, [prospect]);

  const fetchInteractions = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BACKEND_URL}/interactions/${prospect._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sorted = res.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setInteractions(sorted);
    } catch (err) {
      console.error("Erreur récupération interactions :", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddInteraction = async () => {
    const { typeInteraction, canal, date, contenu } = formData;
    if (!typeInteraction || !canal || !date || !contenu) {
      alert("Tous les champs obligatoires doivent être remplis.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${BACKEND_URL}/interactions`,
        {
          ...formData,
          prospectId: prospect._id,
          referent: prospect.referent,
          statut: prospect.statut,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      fetchInteractions();
      setShowForm(false);
      setFormData({
        typeInteraction: "",
        canal: "",
        date: "",
        contenu: "",
        prochaineEtape: "",
        dateProchaineInteraction: "",
        important: false,
      });
    } catch (err) {
      console.error("Erreur ajout interaction :", err);
    }
  };

  const handleDelete = async (id) => {
    const confirm = window.confirm("Supprimer cette interaction ?");
    if (!confirm) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${BACKEND_URL}/interactions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchInteractions();
    } catch (err) {
      console.error("Erreur suppression :", err);
    }
  };

  // 🔁 Déterminer l’étape atteinte selon le statut
  const getCurrentStep = () => {
    switch (prospect?.statut) {
      case "Prospect":
        return 0;
      case "Prospect Qualifié":
        return 1;
      case "Client":
        return 2;
      default:
        return 0;
    }
  };

  const getStepIndex = () => {
    switch (prospect?.statut) {
      case "Lead":
      case "Prospect":
        return 0;
      case "Prospect Qualifié":
        return 1;
      case "Client":
        return 2;
      default:
        return 0;
    }
  };
  
  const getProgressBarColor = () => {
    const step = getStepIndex();
    return step === 2 ? "bg-green-500" : "bg-blue-500";
  };
  
  const getProgressBarWidth = () => {
    const step = getStepIndex();
    return step === 0 ? "w-1/3" : step === 1 ? "w-2/3" : "w-full";
  };  

  if (!prospect) {
    return (
      <div className="flex flex-col items-center justify-center text-center bg-white shadow-md rounded-lg h-[300px] w-[800px] p-6 space-y-4">
        <img src={logo} alt="Logo Nanthiat" className="w-20 h-20" />
        <p className="text-gray-500">Sélectionnez un prospect pour voir son historique d’interactions.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6 w-[800px] h-[300px] flex flex-col justify-between">
      {/* ✅ Haut : Liste des interactions */}
      <div className="flex-1 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Dernières interactions</h2>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
          >
            <PlusIcon className="w-5 h-5" />
            Ajouter
          </button>
        </div>

        {interactions.length > 0 ? (
          <div className="space-y-3 pr-1 max-h-[100px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
            {interactions.map((item) => (
              <div
                key={item._id}
                className="relative border-l-4 border-blue-500 pl-4 py-2 bg-gray-50 rounded-md shadow-sm"
              >
                <div className="flex justify-between">
                  <p className="text-sm text-gray-800 font-medium">
                    {item.typeInteraction} via {item.canal} –{" "}
                    {new Date(item.date).toLocaleDateString()}
                  </p>
                  <button
                    onClick={() => handleDelete(item._id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-600">{item.contenu}</p>

                {item.prochaineEtape && (
                  <p className="text-sm text-blue-600 mt-1">
                    📌 {item.prochaineEtape}{" "}
                    {item.dateProchaineInteraction &&
                      `(le ${new Date(item.dateProchaineInteraction).toLocaleDateString()})`}
                  </p>
                )}

                {item.important && (
                  <div className="absolute top-1 right-10 text-yellow-500">
                    <ExclamationTriangleIcon className="w-5 h-5" title="Important" />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Aucune interaction récente</p>
        )}
      </div>

      {/* ✅ Bas : Progress bar */}
      <div className="mt-auto">
        <h3 className="text-sm font-medium text-gray-700 mb-4">
          Avancement de l'onboarding
        </h3>

        <div className="relative flex justify-between items-center w-full h-10">
          {/* Barre de fond */}
          <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-200 transform -translate-y-1/2 rounded-full z-0" />

          {/* Barre de progression */}
          <div
            className={`absolute top-1/2 left-0 h-1 transition-all duration-700 ease-in-out rounded-full z-10 ${getProgressBarColor()} ${getProgressBarWidth()}`}
            style={{ transform: "translateY(-50%)" }}
          />

          {/* Étapes */}
          {["Ajout", "Lettre d'Entrée en Relation", "Audit Patrimonial"].map(
            (label, idx) => {
              const isCompleted = getStepIndex() > idx;
              const isCurrent = getStepIndex() === idx;

              return (
                <div key={idx} className="z-20 flex flex-col items-center flex-1">
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full border-2
                    ${isCompleted
                        ? "bg-green-500 border-green-500 text-white"
                        : isCurrent
                        ? "bg-white border-blue-500 text-blue-500"
                        : "bg-white border-gray-300 text-gray-400"}
                    transition duration-300`}
                  >
                    {isCompleted ? (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="text-sm font-semibold">{idx + 1}</span>
                    )}
                  </div>
                  <span className="text-[11px] text-center text-gray-600 mt-2 w-24">
                    {label}
                  </span>
                </div>
              );
            }
          )}
        </div>
      </div>

      {/* Modal personnalisé */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold">Nouvelle interaction</h3>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium">Type d'interaction *</label>
                <select
                  name="typeInteraction"
                  value={formData.typeInteraction}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="">-- Sélectionner --</option>
                  {["Appel", "Email", "Rendez-vous", "Message LinkedIn", "Note interne", "Autre"].map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium">Canal *</label>
                <select
                  name="canal"
                  value={formData.canal}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="">-- Sélectionner --</option>
                  {["Téléphone", "Email", "LinkedIn", "Visio", "WhatsApp", "Autre"].map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium">Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Contenu *</label>
                <textarea
                  name="contenu"
                  value={formData.contenu}
                  onChange={handleChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md p-2"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium">Prochaine étape</label>
                <input
                  name="prochaineEtape"
                  value={formData.prochaineEtape}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Date prochaine interaction</label>
                <input
                  type="date"
                  name="dateProchaineInteraction"
                  value={formData.dateProchaineInteraction}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="important"
                  checked={formData.important}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600"
                />
                <label className="text-sm">Marquer comme important</label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg"
                onClick={() => setShowForm(false)}
              >
                Annuler
              </button>
              <button
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                onClick={handleAddInteraction}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
